import { type CSSProperties, useCallback } from 'react'
import cx from 'classnames'
import { observer } from 'mobx-react-lite'
import { Button as AriaButton } from 'react-aria-components'
import { Icon } from '@/components/Icon'
import { usePageBottomOffset } from '@/contexts/page-bottom-offset-context'
import { useIsShowingModal } from '@/components/ModalDialog/ModalStackContext'
import { useIsSpacious } from '@/hooks/viewport-hooks'
import { useFeatures } from '@/contexts/root-store-context'
import { externalLinks } from '@/utils/link'
import { normalizeCssNumber } from '@/utils/browser-util'
import styles from './ChatSupportFallbackButton.module.css'

export const ChatSupportFallbackButton = observer(
  function ChatSupportFallbackButton() {
    const offset = usePageBottomOffset()
    const isShowingModal = useIsShowingModal()
    const isSpacious = useIsSpacious()
    const shouldHide = isShowingModal || !isSpacious
    const {
      shell: { getExternalUrlDelegate },
    } = useFeatures(({ shell }) => ({ shell }))

    const handlePress = useCallback(() => {
      const handle = getExternalUrlDelegate().createHandle()
      handle.open(externalLinks.contactSupport())
    }, [getExternalUrlDelegate])

    return (
      <AriaButton
        aria-label="Contact support"
        className={({ isHovered, isPressed, isFocusVisible }) =>
          cx(
            styles.root,
            isHovered && styles.hover,
            isPressed && styles.pressed,
            isFocusVisible && styles.focusVisible,
            shouldHide && styles.hidden,
          )
        }
        style={
          {
            '--bottom-offset': normalizeCssNumber(offset) ?? '24px',
          } as CSSProperties
        }
        onPress={handlePress}
      >
        <Icon name="support" variant="inherit" />
      </AriaButton>
    )
  },
)
